"use client"

import { useEffect, useId, useState, type ReactNode } from "react"
import { MenuIcon, XIcon } from "lucide-react"

import { DARK_BAND_STYLE } from "@/components/showcase/band-tokens"
import { Link, usePathname } from "@/i18n/navigation"

export interface ShowcaseMobileMenuLink {
  href: string
  label: string
  /** A plain `<a>` (a restaurant-hosted legal document), not a locale route. */
  external?: boolean
}

/**
 * The `showcase` phone header's menu, after the reference sites' mobile
 * template: the three-bar button turns into a cross and opens a 90% black
 * panel under the header, the page links in bold white rows split by thin
 * grey rules. `children` (order-type toggle, theme and language controls)
 * render under the links.
 *
 * Not a Sheet: the header — with this button, now a cross — stays visible
 * and live above the panel, as on the reference. Closes on navigation and
 * Escape, and locks the page scroll while open.
 */
export function ShowcaseMobileMenu({
  links,
  openLabel,
  closeLabel,
  menuLabel,
  children,
}: {
  links: ShowcaseMobileMenuLink[]
  openLabel: string
  closeLabel: string
  menuLabel: string
  children?: ReactNode
}) {
  const pathname = usePathname()
  // The path the menu was opened on: any navigation closes it, whatever
  // started it (a menu link, the basket, search).
  const [openOn, setOpenOn] = useState<string | null>(null)
  const open = openOn === pathname
  const panelId = useId()

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenOn(null)
    }
    const overflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", onKey)
    return () => {
      document.body.style.overflow = overflow
      window.removeEventListener("keydown", onKey)
    }
  }, [open])

  const close = () => setOpenOn(null)

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? closeLabel : openLabel}
        onClick={() => setOpenOn(open ? null : pathname)}
        className="grid size-10 shrink-0 place-items-center text-foreground"
      >
        {open ? (
          <XIcon className="size-8" strokeWidth={3} aria-hidden />
        ) : (
          <MenuIcon className="size-8" strokeWidth={3} aria-hidden />
        )}
      </button>

      <div
        id={panelId}
        hidden={!open}
        className="fixed inset-x-0 top-(--showcase-mobile-header) bottom-0 z-40 overflow-y-auto overscroll-contain bg-black/90 text-white"
      >
        <nav aria-label={menuLabel}>
          <ul>
            {links.map((link) => (
              <li key={link.href} className="border-b border-white/45">
                {link.external ? (
                  <a
                    href={link.href}
                    onClick={close}
                    className="block px-[30px] py-[9px] text-[17px] leading-[19px] font-bold"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    href={link.href}
                    onClick={close}
                    className="block px-[30px] py-[9px] text-[17px] leading-[19px] font-bold"
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
        {children ? (
          <div
            style={DARK_BAND_STYLE}
            className="flex flex-wrap items-center gap-3 px-[30px] py-5"
          >
            {children}
          </div>
        ) : null}
      </div>
    </>
  )
}
